// pages/SuperAdminCompaniesPage.js
import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import {
  getFirestore,
  collection,
  query,
  orderBy,
  onSnapshot
} from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { app } from '../firebase'
import './SuperAdminCompaniesPage.css'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import WarningIcon from '@mui/icons-material/Warning'
import ImageIcon from '@mui/icons-material/Image'
import toast from 'react-hot-toast'
import CompanyImageUploadModal from '../components/CompanyImageUploadModal'

function SuperAdminCompaniesPage () {
  const { user } = useAuth()
  const [companies, setCompanies] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [creating, setCreating] = useState(false)
  const [successMessage, setSuccessMessage] = useState(null)
  const [createdInviteCode, setCreatedInviteCode] = useState(null)

  // Action states
  const [togglingStatus, setTogglingStatus] = useState(null) // companyId being toggled
  const [deleteModal, setDeleteModal] = useState({ open: false, company: null })
  const [deleteConfirmName, setDeleteConfirmName] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [editingCompany, setEditingCompany] = useState(null) // companyId being edited
  const [updating, setUpdating] = useState(false)
  const [uploadModal, setUploadModal] = useState({ open: false, company: null, imageType: null })

  // Form data for creating new company
  const [formData, setFormData] = useState({
    companyName: '',
    companyId: '',
    description: '',
    pineconeIndexName: ''
  })

  const db = getFirestore(app)
  const functions = getFunctions(app)

  useEffect(() => {
    if (!user) {
      setLoading(false)
      setError('Bitte melden Sie sich an')
      return
    }

    let unsubscribe = () => {}

    const setupListener = async () => {
      try {
        // Real-time listener for all companies
        const companiesRef = collection(db, 'companies')
        const q = query(companiesRef, orderBy('createdAt', 'desc'))

        unsubscribe = onSnapshot(
          q,
          snapshot => {
            const companiesData = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }))
            setCompanies(companiesData)
            setLoading(false)
          },
          err => {
            console.error('Error loading companies:', err)
            setError('Fehler beim Laden der Companies')
            setLoading(false)
          }
        )
      } catch (err) {
        console.error('Error setting up listener:', err)
        setError('Fehler beim Initialisieren')
        setLoading(false)
      }
    }

    setupListener()

    return () => unsubscribe()
  }, [db, user])

  const handleCreateCompany = async e => {
    e.preventDefault()
    setCreating(true)
    setError(null)
    setSuccessMessage(null)
    setCreatedInviteCode(null)

    try {
      const createCompany = httpsCallable(functions, 'createCompany')
      const result = await createCompany({
        companyName: formData.companyName,
        companyId: formData.companyId,
        description: formData.description || undefined,
        pineconeIndexName: formData.pineconeIndexName || undefined
      })

      if (result.data.success) {
        setSuccessMessage(
          `Company "${result.data.companyName}" erfolgreich erstellt!`
        )
        if (result.data.inviteCode) {
          setCreatedInviteCode(result.data.inviteCode)
        }

        // Reset form
        setFormData({
          companyName: '',
          companyId: '',
          description: '',
          pineconeIndexName: ''
        })
        setShowCreateForm(false)
      } else {
        setError(result.data.message || 'Fehler beim Erstellen der Company')
      }
    } catch (error) {
      console.error('Error creating company:', error)
      setError(
        error.message ||
          'Fehler beim Erstellen der Company. Bitte versuchen Sie es erneut.'
      )
    } finally {
      setCreating(false)
    }
  }

  const generateCompanyId = () => {
    if (formData.companyName) {
      const id = formData.companyName
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')
      setFormData({ ...formData, companyId: id })
    }
  }

  const formatDate = timestamp => {
    if (!timestamp) return 'N/A'
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
    return date.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const copyToClipboard = text => {
    navigator.clipboard.writeText(text)
    toast.success('Text in die Zwischenablage kopiert!')
  }

  const handleToggleStatus = async (companyId) => {
    setTogglingStatus(companyId)
    setError(null)

    try {
      const toggleCompanyStatus = httpsCallable(functions, 'toggleCompanyStatus')
      const result = await toggleCompanyStatus({ companyId })

      if (result.data.success) {
        toast.success(result.data.message)
      } else {
        setError(result.data.message || 'Fehler beim Ändern des Status')
      }
    } catch (error) {
      console.error('Error toggling company status:', error)
      setError(error.message || 'Fehler beim Ändern des Status')
      toast.error('Fehler beim Ändern des Status')
    } finally {
      setTogglingStatus(null)
    }
  }

  const openDeleteModal = (company) => {
    setDeleteModal({ open: true, company })
    setDeleteConfirmName('')
  }

  const closeDeleteModal = () => {
    setDeleteModal({ open: false, company: null })
    setDeleteConfirmName('')
  }

  const handleDeleteCompany = async () => {
    if (!deleteModal.company) return

    setDeleting(true)
    setError(null)

    try {
      const deleteCompany = httpsCallable(functions, 'deleteCompany')
      const result = await deleteCompany({
        companyId: deleteModal.company.id,
        confirmationName: deleteConfirmName
      })

      if (result.data.success) {
        toast.success(result.data.message)
        const stats = result.data.deletedData
        const subStats = stats.subcollections || {}
        const totalDocs = Object.values(subStats).reduce((a, b) => a + b, 0)
        setSuccessMessage(
          `Company "${result.data.companyName}" wurde vollständig gelöscht. ` +
          `(${stats.globalUsers} User, ${stats.authUsers} Auth-Accounts, ` +
          `${totalDocs} Dokumente in ${Object.keys(subStats).length} Collections)`
        )
        closeDeleteModal()
      } else {
        setError(result.data.message || 'Fehler beim Löschen der Company')
      }
    } catch (error) {
      console.error('Error deleting company:', error)
      setError(error.message || 'Fehler beim Löschen der Company')
      toast.error(error.message || 'Fehler beim Löschen der Company')
    } finally {
      setDeleting(false)
    }
  }

  const handleUpdateCompany = async (companyId, updates) => {
    setUpdating(true)
    setError(null)

    try {
      const updateCompany = httpsCallable(functions, 'updateCompany')
      const result = await updateCompany({
        companyId,
        ...updates
      })

      if (result.data.success) {
        toast.success(result.data.message)
        setEditingCompany(null)
      } else {
        setError(result.data.message || 'Fehler beim Aktualisieren der Company')
      }
    } catch (error) {
      console.error('Error updating company:', error)
      setError(error.message || 'Fehler beim Aktualisieren der Company')
      toast.error(error.message || 'Fehler beim Aktualisieren der Company')
    } finally {
      setUpdating(false)
    }
  }

  const handlePlanChange = async (companyId, newPlan) => {
    await handleUpdateCompany(companyId, { plan: newPlan })
  }

  const handleFeatureToggle = async (companyId, featureName, enabled, company) => {
    // Features that require image upload
    const imageFeatures = ['imageHeader', 'logo']
    
    if (imageFeatures.includes(featureName) && enabled) {
      // Open upload modal when enabling image features
      setUploadModal({ open: true, company, imageType: featureName === 'imageHeader' ? 'header' : 'logo' })
      // Don't update the feature yet - wait for image upload
      return
    }
    
    // For disabling or other features, update immediately
    await handleUpdateCompany(companyId, {
      features: { [featureName]: enabled }
    })
  }

  const openUploadModal = (company, imageType) => {
    setUploadModal({ open: true, company, imageType })
  }

  const closeUploadModal = () => {
    setUploadModal({ open: false, company: null, imageType: null })
  }

  const handleUploadSuccess = async () => {
    if (!uploadModal.company || !uploadModal.imageType) return
    
    const companyId = uploadModal.company.id
    const featureName = uploadModal.imageType === 'header' ? 'imageHeader' : 'logo'
    
    // Enable the feature after successful upload
    await handleUpdateCompany(companyId, {
      features: { [featureName]: true }
    })
  }

  if (loading) {
    return (
      <div className='superadmin-companies'>
        <div className='loading'>Lädt Companies...</div>
      </div>
    )
  }

  return (
    <div className='superadmin-companies'>
      <div className='page-header'>
        <div>
          <h1>🏢 Company Management</h1>
          <p className='page-subtitle'>
            {companies.length} Companies im System
          </p>
        </div>
        <button
          className='btn-primary'
          onClick={() => setShowCreateForm(!showCreateForm)}
        >
          {showCreateForm ? 'Abbrechen' : '+ Neue Company'}
        </button>
      </div>

      {error && (
        <div className='error-message'>
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {successMessage && (
        <div className='success-message'>
          <div>
            <strong>✓ Erfolg!</strong> {successMessage}
          </div>
          {createdInviteCode && (
            <div className='invite-code-display'>
              <strong>Admin Invite-Code:</strong>
              <code>{createdInviteCode}</code>
              <button
                className='btn-icon'
                onClick={() => copyToClipboard(createdInviteCode)}
                title='Code kopieren'
              >
                <ContentCopyIcon fontSize='small' />
              </button>
            </div>
          )}
          <button onClick={() => setSuccessMessage(null)}>×</button>
        </div>
      )}

      {showCreateForm && (
        <div className='create-form-card'>
          <h2>Neue Company erstellen</h2>
          <form onSubmit={handleCreateCompany} className='create-form'>
            <div className='form-group'>
              <label htmlFor='companyName'>
                Company Name <span className='required'>*</span>
              </label>
              <input
                type='text'
                id='companyName'
                value={formData.companyName}
                onChange={e =>
                  setFormData({ ...formData, companyName: e.target.value })
                }
                onBlur={generateCompanyId}
                placeholder='z.B. "Asclepion Laser Technologies"'
                required
                disabled={creating}
              />
            </div>

            <div className='form-group'>
              <label htmlFor='companyId'>
                Company ID <span className='required'>*</span>
              </label>
              <input
                type='text'
                id='companyId'
                value={formData.companyId}
                onChange={e =>
                  setFormData({ ...formData, companyId: e.target.value })
                }
                placeholder='z.B. "asclepion" (wird automatisch generiert)'
                required
                disabled={creating}
                pattern='[a-z0-9-]+'
                title='Nur Kleinbuchstaben, Zahlen und Bindestriche'
              />
              <small>
                Wird automatisch aus dem Namen generiert. Nur Kleinbuchstaben,
                Zahlen und Bindestriche.
              </small>
            </div>

            <div className='form-group'>
              <label htmlFor='description'>Beschreibung (Optional)</label>
              <input
                type='text'
                id='description'
                value={formData.description}
                onChange={e =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder='z.B. "Für neuen Admin Max Mustermann"'
                disabled={creating}
              />
              <small>
                Optional: Wird in der Beschreibung des Invite-Codes angezeigt
              </small>
            </div>

            <div className='form-group'>
              <label htmlFor='pineconeIndexName'>
                Pinecone Index Name (Optional)
              </label>
              <input
                type='text'
                id='pineconeIndexName'
                value={formData.pineconeIndexName}
                onChange={e =>
                  setFormData({
                    ...formData,
                    pineconeIndexName: e.target.value
                  })
                }
                placeholder='altknowledge-companyid (automatisch generiert)'
                disabled={creating}
              />
              <small>
                Leer lassen für automatische Generierung:
                altknowledge-[company-id]
              </small>
            </div>

            <button type='submit' className='btn-primary' disabled={creating}>
              {creating ? 'Erstelle Company...' : 'Company erstellen'}
            </button>
          </form>
        </div>
      )}

      <div className='companies-list'>
        {companies.length === 0 ? (
          <div className='empty-state'>
            <p>Noch keine Companies im System.</p>
            <p>Erstellen Sie eine neue Company, um zu beginnen.</p>
          </div>
        ) : (
          <div className='companies-grid'>
            {companies.map(company => (
              <div key={company.id} className={`superadmin-company-card ${company.status !== 'active' ? 'inactive-company' : ''}`}>
                <div className='superadmin-company-header'>
                  <div className='superadmin-company-header-content'>
                    <h3 className='superadmin-company-name'>{company.name}</h3>
                    <div className='superadmin-company-id'>ID: {company.id}</div>
                  </div>
                  <span
                    className={`superadmin-status-badge ${
                      company.status === 'active' ? 'active' : 'inactive'
                    }`}
                  >
                    {company.status === 'active' ? 'Aktiv' : 'Inaktiv'}
                  </span>
                </div>

                <div className='superadmin-company-details'>
                  <div className='superadmin-detail-row'>
                    <span className='superadmin-detail-label'>Erstellt</span>
                    <span className='superadmin-detail-value'>
                      {formatDate(company.createdAt)}
                    </span>
                  </div>

                  <div className='superadmin-detail-row editable-row'>
                    <span className='superadmin-detail-label'>Plan</span>
                    {editingCompany === company.id ? (
                      <select
                        className='superadmin-plan-select'
                        value={company.plan || 'pro'}
                        onChange={e => handlePlanChange(company.id, e.target.value)}
                        disabled={updating}
                      >
                        <option value='free'>Free</option>
                        <option value='pro'>Pro</option>
                        <option value='enterprise'>Enterprise</option>
                      </select>
                    ) : (
                      <span className='superadmin-detail-value'>{company.plan || 'pro'}</span>
                    )}
                  </div>

                  {company.settings?.pineconeIndexName && (
                    <div className='superadmin-detail-row'>
                      <span className='superadmin-detail-label'>Pinecone Index</span>
                      <span className='superadmin-detail-value'>
                        {company.settings.pineconeIndexName}
                      </span>
                    </div>
                  )}

                  {company.settings?.maxUsers && (
                    <div className='superadmin-detail-row'>
                      <span className='superadmin-detail-label'>Max. Users</span>
                      <span className='superadmin-detail-value'>{company.settings.maxUsers}</span>
                    </div>
                  )}
                </div>

                {/* Features Section */}
                <div className='superadmin-company-features'>
                  <div className='superadmin-features-header'>
                    <span className='superadmin-features-label'>Features</span>
                    {editingCompany === company.id ? (
                      <button
                        className='superadmin-btn-save-features'
                        onClick={() => setEditingCompany(null)}
                        disabled={updating}
                      >
                        Fertig
                      </button>
                    ) : (
                      <button
                        className='superadmin-btn-edit-features'
                        onClick={() => setEditingCompany(company.id)}
                        disabled={updating || togglingStatus === company.id}
                      >
                        Bearbeiten
                      </button>
                    )}
                  </div>
                  {editingCompany === company.id && (
                    <div className='superadmin-features-list'>
                      <div className='superadmin-feature-toggle'>
                        <label>
                          <input
                            type='checkbox'
                            checked={company.features?.imageHeader || false}
                            onChange={e =>
                              handleFeatureToggle(
                                company.id,
                                'imageHeader',
                                e.target.checked,
                                company
                              )
                            }
                            disabled={updating}
                          />
                          <span>Image Header</span>
                        </label>
                        {company.features?.imageHeader && (
                          <button
                            className='superadmin-feature-edit-btn'
                            onClick={() => openUploadModal(company, 'header')}
                            disabled={updating}
                            title='Header-Bild bearbeiten'
                          >
                            <ImageIcon fontSize='small' />
                          </button>
                        )}
                      </div>
                      <div className='superadmin-feature-toggle'>
                        <label>
                          <input
                            type='checkbox'
                            checked={company.features?.logo || false}
                            onChange={e =>
                              handleFeatureToggle(
                                company.id,
                                'logo',
                                e.target.checked,
                                company
                              )
                            }
                            disabled={updating}
                          />
                          <span>Logo</span>
                        </label>
                        {company.features?.logo && (
                          <button
                            className='superadmin-feature-edit-btn'
                            onClick={() => openUploadModal(company, 'logo')}
                            disabled={updating}
                            title='Logo bearbeiten'
                          >
                            <ImageIcon fontSize='small' />
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  {editingCompany !== company.id && (
                    <div className='superadmin-features-display'>
                      {company.features?.imageHeader || company.features?.logo ? (
                        <>
                          {company.features?.imageHeader && (
                            <div className='superadmin-feature-badge-with-action'>
                              <span className='superadmin-feature-badge-text'>Image Header</span>
                              <button
                                className='superadmin-feature-edit-btn-small'
                                onClick={() => openUploadModal(company, 'header')}
                                disabled={updating || togglingStatus === company.id}
                                title='Header-Bild bearbeiten'
                              >
                                <ImageIcon fontSize='small' />
                              </button>
                            </div>
                          )}
                          {company.features?.logo && (
                            <div className='superadmin-feature-badge-with-action'>
                              <span className='superadmin-feature-badge-text'>Logo</span>
                              <button
                                className='superadmin-feature-edit-btn-small'
                                onClick={() => openUploadModal(company, 'logo')}
                                disabled={updating || togglingStatus === company.id}
                                title='Logo bearbeiten'
                              >
                                <ImageIcon fontSize='small' />
                              </button>
                            </div>
                          )}
                        </>
                      ) : (
                        <span className='superadmin-feature-badge-empty'>Keine Features aktiv</span>
                      )}
                    </div>
                  )}
                </div>

                <div className='superadmin-company-actions'>
                  <button
                    className={`superadmin-btn-action ${company.status === 'active' ? 'superadmin-btn-deactivate' : 'superadmin-btn-activate'}`}
                    onClick={() => handleToggleStatus(company.id)}
                    disabled={togglingStatus === company.id}
                    title={company.status === 'active' ? 'Company deaktivieren' : 'Company aktivieren'}
                  >
                    <PowerSettingsNewIcon fontSize='small' />
                    {togglingStatus === company.id
                      ? 'Lädt...'
                      : company.status === 'active'
                        ? 'Deaktivieren'
                        : 'Aktivieren'}
                  </button>
                  <button
                    className='superadmin-btn-action superadmin-btn-delete'
                    onClick={() => openDeleteModal(company)}
                    disabled={togglingStatus === company.id}
                    title='Company löschen'
                  >
                    <DeleteForeverIcon fontSize='small' />
                    Löschen
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal.open && deleteModal.company && (
        <div className='modal-overlay' onClick={closeDeleteModal}>
          <div className='modal-content delete-modal' onClick={e => e.stopPropagation()}>
            <div className='modal-header danger'>
              <WarningIcon className='warning-icon' />
              <h2>Company löschen</h2>
            </div>

            <div className='modal-body'>
              <div className='danger-warning'>
                <strong>Achtung! Diese Aktion kann nicht rückgängig gemacht werden!</strong>
              </div>

              <p>Sie sind dabei, die folgende Company <strong>dauerhaft</strong> zu löschen:</p>

              <div className='company-to-delete'>
                <div className='company-name'>{deleteModal.company.name}</div>
                <div className='company-id'>ID: {deleteModal.company.id}</div>
              </div>

              <div className='deletion-info'>
                <p>Folgende Daten werden <strong>unwiderruflich gelöscht</strong>:</p>
                <ul>
                  <li>Alle Company-User und deren Zuordnungen</li>
                  <li>Alle Invite-Codes</li>
                  <li>Alle Knowledge-Einträge</li>
                  <li>Alle Drafts</li>
                </ul>
              </div>

              <div className='confirmation-input'>
                <label>
                  Zur Bestätigung geben Sie den Company-Namen ein:
                  <strong> {deleteModal.company.name}</strong>
                </label>
                <input
                  type='text'
                  value={deleteConfirmName}
                  onChange={e => setDeleteConfirmName(e.target.value)}
                  placeholder='Company-Name eingeben...'
                  disabled={deleting}
                  autoFocus
                />
              </div>
            </div>

            <div className='modal-actions'>
              <button
                className='btn-cancel'
                onClick={closeDeleteModal}
                disabled={deleting}
              >
                Abbrechen
              </button>
              <button
                className='btn-delete-confirm'
                onClick={handleDeleteCompany}
                disabled={deleting || deleteConfirmName !== deleteModal.company.name}
              >
                {deleting ? 'Lösche...' : 'Endgültig löschen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Upload Modal */}
      <CompanyImageUploadModal
        open={uploadModal.open}
        company={uploadModal.company}
        imageType={uploadModal.imageType}
        onClose={closeUploadModal}
        onSuccess={handleUploadSuccess}
      />
    </div>
  )
}

export default SuperAdminCompaniesPage
